import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCountdownResult {
  /** Seconds left, floored at zero. */
  remaining: number;
  expired: boolean;
  /** Restart the countdown from the initial value. */
  reset: () => void;
}

/**
 * One-second countdown from `seconds` to zero.
 * `onExpire` fires once when the countdown reaches zero.
 */
export function useCountdown(seconds: number, onExpire?: () => void): UseCountdownResult {
  const [remaining, setRemaining] = useState(seconds);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const running = remaining > 0;

  useEffect(() => {
    if (!running) return undefined;
    // Updater stays pure (no callbacks inside) - expiry fires from the effect below.
    const id = setInterval(() => setRemaining((current) => Math.max(current - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Fire onExpire exactly once per countdown, on the transition to zero.
  const expireFired = useRef(false);
  useEffect(() => {
    if (running) {
      expireFired.current = false;
    } else if (!expireFired.current) {
      expireFired.current = true;
      onExpireRef.current?.();
    }
  }, [running]);

  const reset = useCallback(() => setRemaining(seconds), [seconds]);

  return { remaining, expired: remaining <= 0, reset };
}
